import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Shield, Check, X, Save, ChevronDown, ChevronRight, Users, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const ROLE_COLORS = {
  admin: 'bg-red-500',
  coordenador: 'bg-purple-500',
  lider_pg: 'bg-blue-500',
  facilitador: 'bg-cyan-500',
  voluntario: 'bg-green-500',
  visualizador: 'bg-gray-500',
};

const ROLE_ICONS = {
  admin: '👑',
  coordenador: '🎯',
  lider_pg: '👥',
  facilitador: '🤝',
  voluntario: '🙋',
  visualizador: '👀',
};

const Permissions = () => {
  const [roles, setRoles] = useState({});
  const [allPermissions, setAllPermissions] = useState({});
  const [permissionCategories, setPermissionCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Selected role for editing
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState([]);
  
  // User custom permissions dialog
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userCustomPermissions, setUserCustomPermissions] = useState({ granted: [], denied: [] });
  
  // Expanded categories
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rolesRes, usersRes] = await Promise.all([
        axios.get(`${API}/users/roles`),
        axios.get(`${API}/users`)
      ]);
      
      setRoles(rolesRes.data.roles || {});
      setAllPermissions(rolesRes.data.all_permissions || {});
      setPermissionCategories(rolesRes.data.permission_categories || []);
      setUsers(usersRes.data || []);
      
      // Expand all categories by default
      const expanded = {};
      (rolesRes.data.permission_categories || []).forEach(cat => {
        expanded[cat] = true;
      });
      setExpandedCategories(expanded);
      
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const selectRole = (roleId) => {
    setSelectedRole(roleId);
    const role = roles[roleId];
    setRolePermissions(role?.permissions || []);
  };

  const togglePermission = (permId) => {
    setRolePermissions(prev => {
      if (prev.includes(permId)) {
        return prev.filter(p => p !== permId);
      } else {
        return [...prev, permId];
      }
    });
  };

  const saveRolePermissions = async () => {
    if (!selectedRole) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/users/roles/${selectedRole}/permissions`, rolePermissions);
      toast.success('Permissões do perfil atualizadas!');
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar permissões');
    } finally {
      setSaving(false);
    }
  };

  const openUserDialog = (user) => {
    setSelectedUser(user);
    setUserCustomPermissions(user.custom_permissions || { granted: [], denied: [] });
    setUserDialogOpen(true);
  };

  const toggleUserGranted = (permId) => {
    setUserCustomPermissions(prev => {
      const granted = prev.granted || [];
      const denied = prev.denied || [];
      
      if (granted.includes(permId)) {
        // Remove from granted
        return { ...prev, granted: granted.filter(p => p !== permId) };
      } else {
        // Add to granted, remove from denied
        return { 
          granted: [...granted, permId], 
          denied: denied.filter(p => p !== permId) 
        };
      }
    });
  };

  const toggleUserDenied = (permId) => {
    setUserCustomPermissions(prev => {
      const granted = prev.granted || [];
      const denied = prev.denied || [];
      
      if (denied.includes(permId)) {
        // Remove from denied
        return { ...prev, denied: denied.filter(p => p !== permId) };
      } else {
        // Add to denied, remove from granted
        return { 
          granted: granted.filter(p => p !== permId), 
          denied: [...denied, permId] 
        };
      }
    });
  };

  const saveUserPermissions = async () => {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/users/${selectedUser.id}/custom-permissions`, userCustomPermissions);
      toast.success('Permissões do usuário atualizadas!');
      setUserDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar permissões');
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Get permissions grouped by category
  const getPermissionsByCategory = () => {
    const grouped = {};
    Object.entries(allPermissions).forEach(([permId, permData]) => {
      const category = permData.category;
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push({ id: permId, ...permData });
    });
    return grouped;
  };

  const permsByCategory = getPermissionsByCategory();

  // Get user's role permissions for comparison
  const getUserRolePermissions = () => {
    if (!selectedUser) return [];
    return roles[selectedUser.role]?.permissions || [];
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Carregando...</div>;
  }

  return (
    <div className="fade-in">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gerenciar Permissões</h1>
          <p className="text-gray-500 mt-1">Configure as permissões de cada perfil e usuário</p>
        </div>
      </div>

      <Tabs defaultValue="roles" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="roles" className="flex items-center gap-2">
            <Shield size={16} />
            Perfis
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users size={16} />
            Usuários
          </TabsTrigger>
        </TabsList>

        {/* ROLES TAB */}
        <TabsContent value="roles">
          <div className="grid grid-cols-12 gap-6">
            {/* Role list */}
            <div className="col-span-4">
              <div className="card">
                <h3 className="font-semibold text-lg mb-4">Perfis</h3>
                <div className="space-y-2">
                  {Object.entries(roles).map(([roleId, role]) => (
                    <button
                      key={roleId}
                      onClick={() => selectRole(roleId)}
                      className={`w-full p-3 rounded-lg text-left transition flex items-center gap-3 ${
                        selectedRole === roleId 
                          ? 'bg-blue-100 border-2 border-blue-500' 
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full ${ROLE_COLORS[roleId]} flex items-center justify-center text-white text-lg`}>
                        {ROLE_ICONS[roleId]}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{role.name}</p>
                        <p className="text-xs text-gray-500">{role.permissions?.length || 0} permissões</p>
                      </div>
                      {role.is_system && (
                        <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">Sistema</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Permission editor */}
            <div className="col-span-8">
              {selectedRole ? (
                <div className="card">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full ${ROLE_COLORS[selectedRole]} flex items-center justify-center text-white text-xl`}>
                        {ROLE_ICONS[selectedRole]}
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{roles[selectedRole]?.name}</h3>
                        <p className="text-sm text-gray-500">{roles[selectedRole]?.description}</p>
                      </div>
                    </div>
                    <Button onClick={saveRolePermissions} disabled={saving} className="btn-primary">
                      <Save size={16} className="mr-2" />
                      {saving ? 'Salvando...' : 'Salvar Alterações'}
                    </Button>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-sm text-gray-600">
                        {rolePermissions.length} de {Object.keys(allPermissions).length} permissões ativas
                      </p>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setRolePermissions(Object.keys(allPermissions))}
                        >
                          Marcar Todas
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setRolePermissions([])}
                        >
                          Desmarcar Todas
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {Object.entries(permsByCategory).map(([category, perms]) => (
                        <div key={category} className="border rounded-lg overflow-hidden">
                          <button
                            onClick={() => toggleCategory(category)}
                            className="w-full p-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition"
                          >
                            <span className="font-medium">{category}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-500">
                                {perms.filter(p => rolePermissions.includes(p.id)).length}/{perms.length}
                              </span>
                              {expandedCategories[category] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                            </div>
                          </button>
                          {expandedCategories[category] && (
                            <div className="p-2 space-y-1">
                              {perms.map(perm => (
                                <label
                                  key={perm.id}
                                  className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={rolePermissions.includes(perm.id)}
                                    onChange={() => togglePermission(perm.id)}
                                    className="w-4 h-4 rounded text-blue-600"
                                  />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium">{perm.name}</p>
                                    <p className="text-xs text-gray-500">{perm.description}</p>
                                  </div>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="card flex items-center justify-center h-64 text-gray-500">
                  <div className="text-center">
                    <Shield size={48} className="mx-auto mb-4 text-gray-300" />
                    <p>Selecione um perfil para editar suas permissões</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* USERS TAB */}
        <TabsContent value="users">
          <div className="card">
            <div className="flex items-center gap-2 mb-4 p-3 bg-blue-50 rounded-lg">
              <Info size={18} className="text-blue-600" />
              <p className="text-sm text-blue-800">
                Aqui você pode adicionar ou remover permissões específicas para cada usuário, 
                independente do perfil atribuído.
              </p>
            </div>

            <table className="table w-full">
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>Perfil</th>
                  <th>Permissões Customizadas</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => {
                  const customPerms = user.custom_permissions || { granted: [], denied: [] };
                  const hasCustom = customPerms.granted?.length > 0 || customPerms.denied?.length > 0;
                  
                  return (
                    <tr key={user.id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium text-white ${ROLE_COLORS[user.role]}`}>
                          {ROLE_ICONS[user.role]} {roles[user.role]?.name || user.role}
                        </span>
                      </td>
                      <td>
                        {hasCustom ? (
                          <div className="flex flex-wrap gap-1">
                            {customPerms.granted?.length > 0 && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                +{customPerms.granted.length} adicionadas
                              </span>
                            )}
                            {customPerms.denied?.length > 0 && (
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                                -{customPerms.denied.length} removidas
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">Padrão do perfil</span>
                        )}
                      </td>
                      <td>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openUserDialog(user)}
                        >
                          Customizar
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* User Custom Permissions Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Permissões Customizadas - {selectedUser?.name}</DialogTitle>
          </DialogHeader>
          
          {selectedUser && (
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="p-3 bg-gray-50 rounded-lg mb-4">
                <p className="text-sm">
                  <strong>Perfil base:</strong> {ROLE_ICONS[selectedUser.role]} {roles[selectedUser.role]?.name}
                  <span className="text-gray-500 ml-2">
                    ({roles[selectedUser.role]?.permissions?.length || 0} permissões)
                  </span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Marque <span className="text-green-600 font-medium">+Adicionar</span> para conceder permissões extras ou 
                  <span className="text-red-600 font-medium ml-1">-Remover</span> para bloquear permissões do perfil.
                </p>
              </div>

              <div className="flex-1 overflow-y-auto space-y-2">
                {Object.entries(permsByCategory).map(([category, perms]) => {
                  const rolePerms = getUserRolePermissions();
                  
                  return (
                    <div key={category} className="border rounded-lg overflow-hidden">
                      <div className="p-3 bg-gray-50 font-medium">{category}</div>
                      <div className="p-2 space-y-1">
                        {perms.map(perm => {
                          const hasInRole = rolePerms.includes(perm.id);
                          const isGranted = userCustomPermissions.granted?.includes(perm.id);
                          const isDenied = userCustomPermissions.denied?.includes(perm.id);
                          
                          return (
                            <div
                              key={perm.id}
                              className={`flex items-center gap-3 p-2 rounded ${
                                isGranted ? 'bg-green-50' : isDenied ? 'bg-red-50' : ''
                              }`}
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium">{perm.name}</p>
                                  {hasInRole && !isDenied && (
                                    <Check size={14} className="text-green-600" />
                                  )}
                                </div>
                                <p className="text-xs text-gray-500">{perm.description}</p>
                              </div>
                              <div className="flex gap-1">
                                {/* Show +Add only if NOT in role permissions */}
                                {!hasInRole && (
                                  <button
                                    onClick={() => toggleUserGranted(perm.id)}
                                    className={`px-2 py-1 text-xs rounded transition ${
                                      isGranted 
                                        ? 'bg-green-500 text-white' 
                                        : 'bg-gray-100 hover:bg-green-100 text-gray-600'
                                    }`}
                                  >
                                    +Adicionar
                                  </button>
                                )}
                                {/* Show -Remove only if IN role permissions */}
                                {hasInRole && (
                                  <button
                                    onClick={() => toggleUserDenied(perm.id)}
                                    className={`px-2 py-1 text-xs rounded transition ${
                                      isDenied 
                                        ? 'bg-red-500 text-white' 
                                        : 'bg-gray-100 hover:bg-red-100 text-gray-600'
                                    }`}
                                  >
                                    -Remover
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-between items-center pt-4 border-t mt-4">
                <div className="text-sm text-gray-500">
                  {userCustomPermissions.granted?.length || 0} adicionadas, {userCustomPermissions.denied?.length || 0} removidas
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setUserDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setUserCustomPermissions({ granted: [], denied: [] })}
                  >
                    Resetar
                  </Button>
                  <Button onClick={saveUserPermissions} disabled={saving} className="btn-primary">
                    {saving ? 'Salvando...' : 'Salvar'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Permissions;
